// @ts-nocheck
import { useState, useEffect } from 'react';
import { Mail, Send, History, FileEdit, FlaskConical } from 'lucide-react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { formatDateTime } from '../lib/utils';

export function EmailCompose() {
  const [clients, setClients] = useState<Record<string, unknown>[]>([]);
  const [history, setHistory] = useState<Record<string, unknown>[]>([]);
  const [tab, setTab] = useState<'compose' | 'history'>('compose');
  const [selectedClients, setSelectedClients] = useState<Set<number>>(new Set());
  const [sending, setSending] = useState(false);
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState('');

  useEffect(() => {
    api.clients({ year: 'all', sort: 'missing_amount', order: 'desc', limit: '200' }).then(res => {
      setClients(res.clients.filter(c => Number(c.missing_count) > 0));
    });
    api.emailHistory({ limit: '50' }).then(res => {
      setHistory(res.emails);
    });
    api.me().then(res => setUserEmail(res.user.email)).catch(() => {});
  }, []);

  const toggleClient = (id: number) => {
    const next = new Set(selectedClients);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedClients(next);
  };

  const handleSend = async () => {
    if (selectedClients.size === 0) return;
    setSending(true);
    setResult(null);
    try {
      const res = await api.bulkSendEmail([...selectedClients], undefined, 'all');
      const data = res as { sent: number; failed: number; skipped: number };
      setResult(`Sent: ${data.sent}, Failed: ${data.failed}, Skipped (rate limit): ${data.skipped}`);
      setSelectedClients(new Set());
      api.emailHistory({ limit: '50' }).then(res2 => setHistory(res2.emails));
    } catch (err) {
      setResult(`Error: ${(err as Error).message}`);
    } finally {
      setSending(false);
    }
  };

  const handleSendTest = async () => {
    if (selectedClients.size === 0) return;
    const recipient = prompt(
      'Send a test email (using the first selected client\'s data) to:',
      userEmail
    );
    if (!recipient || !recipient.trim()) return;

    const firstClientId = [...selectedClients][0];
    setTesting(true);
    setResult(null);
    try {
      const res = await api.testEmail({ to: recipient.trim(), clientId: firstClientId });
      const c = clients.find(x => x.id === firstClientId);
      setResult(`Test email sent to ${res.sentTo} using ${(c?.name as string) || 'first selected client'}'s data. Check your inbox before sending to clients.`);
    } catch (err) {
      setResult(`Test send error: ${(err as Error).message}`);
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h1 className="text-2xl font-bold">Email Management</h1>
          <span className="text-sm text-gray-500">All years — one email per client covers every missing certificate</span>
        </div>
        <div className="flex border rounded-lg overflow-hidden">
          <button
            onClick={() => setTab('compose')}
            className={`flex items-center gap-2 px-4 py-2 text-sm ${tab === 'compose' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
          >
            <Mail size={14} /> Compose
          </button>
          <button
            onClick={() => setTab('history')}
            className={`flex items-center gap-2 px-4 py-2 text-sm ${tab === 'history' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
          >
            <History size={14} /> History
          </button>
        </div>
      </div>

      {result && (
        <div className={`border rounded-lg p-4 text-sm ${result.startsWith('Error') ? 'bg-red-50 border-red-200 text-red-700' : 'bg-green-50 border-green-200 text-green-700'}`}>
          {result}
        </div>
      )}

      {tab === 'compose' && (
        <div className="space-y-4">
          <div className="bg-white rounded-lg border p-5">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h3 className="font-semibold mb-1">Send Certificate Requests</h3>
                <p className="text-sm text-gray-500">
                  Select clients below to send WHT certificate request emails.
                  Only clients with email addresses and missing certificates are shown.
                </p>
                <p className="text-xs text-amber-600 mt-2">Rate limit: 10 emails per hour</p>
              </div>
              <Link
                to="/wht/templates"
                className="flex items-center gap-2 px-3 py-1.5 border rounded-lg text-sm text-gray-700 hover:bg-gray-50"
              >
                <FileEdit size={14} /> Edit Email Template
              </Link>
            </div>
          </div>

          {selectedClients.size > 0 && (
            <div className="flex items-center gap-3 bg-blue-50 rounded-lg p-4 flex-wrap">
              <span className="text-sm text-blue-700">{selectedClients.size} clients selected</span>
              <button
                onClick={handleSendTest}
                disabled={testing || sending}
                className="flex items-center gap-2 px-4 py-2 border border-blue-600 text-blue-700 bg-white rounded-lg text-sm hover:bg-blue-100 disabled:opacity-50"
                title="Send a test email to yourself first using the first selected client's real data"
              >
                <FlaskConical size={14} /> {testing ? 'Sending test...' : 'Send Test to Me First'}
              </button>
              <button
                onClick={handleSend}
                disabled={sending || testing}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50"
              >
                <Send size={14} /> {sending ? 'Sending...' : 'Send Emails'}
              </button>
            </div>
          )}

          <div className="bg-white rounded-lg border">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={selectedClients.size === clients.filter(c => c.contact_email).length && clients.length > 0}
                      onChange={() => {
                        const withEmail = clients.filter(c => c.contact_email).map(c => c.id as number);
                        setSelectedClients(selectedClients.size === withEmail.length ? new Set() : new Set(withEmail));
                      }}
                      className="rounded border-gray-300"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs text-gray-500 uppercase">Client</th>
                  <th className="px-4 py-3 text-left text-xs text-gray-500 uppercase">Email</th>
                  <th className="px-4 py-3 text-center text-xs text-gray-500 uppercase">Missing Certs</th>
                  <th className="px-4 py-3 text-right text-xs text-gray-500 uppercase">Missing Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {clients.map(c => (
                  <tr key={c.id as number} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      {c.contact_email ? (
                        <input type="checkbox" checked={selectedClients.has(c.id as number)} onChange={() => toggleClient(c.id as number)} className="rounded border-gray-300" />
                      ) : (
                        <span className="text-gray-300">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 font-medium">{c.name as string}</td>
                    <td className="px-4 py-3 text-gray-500">{(c.contact_email as string) || <span className="text-red-400">No email</span>}</td>
                    <td className="px-4 py-3 text-center">{c.missing_count as number}</td>
                    <td className="px-4 py-3 text-right font-mono text-red-600">
                      {Number(c.missing_amount) > 0 ? `฿${Number(c.missing_amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}` : '-'}
                    </td>
                  </tr>
                ))}
                {clients.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-gray-400">
                      No clients with missing certificates found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'history' && (
        <div className="bg-white rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs text-gray-500 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs text-gray-500 uppercase">Client</th>
                <th className="px-4 py-3 text-left text-xs text-gray-500 uppercase">To</th>
                <th className="px-4 py-3 text-left text-xs text-gray-500 uppercase">Subject</th>
                <th className="px-4 py-3 text-center text-xs text-gray-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {history.map(e => (
                <tr key={e.id as number} className="hover:bg-gray-50">
                  <td className="px-4 py-3 whitespace-nowrap">{formatDateTime(e.created_at as string)}</td>
                  <td className="px-4 py-3 font-medium">{e.client_name as string}</td>
                  <td className="px-4 py-3 text-gray-500">{e.sent_to as string}</td>
                  <td className="px-4 py-3 text-gray-600 truncate max-w-xs">{e.subject as string}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`text-xs px-2 py-0.5 rounded ${e.status === 'sent' ? 'bg-green-100 text-green-700' : e.status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>
                      {e.status as string}
                    </span>
                  </td>
                </tr>
              ))}
              {history.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-gray-400">No emails sent yet</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
